import { computed, toValue } from 'vue';
import {
	CRITERIA_VERSION,
	CRITERION_BUCKET,
	CRITERION_KEY,
	CRITERION_LABELS,
	DEFAULT_CRITERIA_ORDER,
	DEFAULT_RANK_ORDER,
	HARD_ONLY_KEYS,
	INACTIVE_KEYS,
} from '../constants/criteria';
import { MAX_EXPERIENCE, MIN_EXPERIENCE } from '../constants/experience';
import { MAX_SALARY, MIN_SALARY } from '../constants/salary';

/**
 * The criteria model behind the Recommendation Settings panel.
 *
 * The panel is one ordered array of `{ key, userBucket }` and everything else is derived.
 * Must-haves and Nice-to-haves are a partition of that array rather than two lists, so
 * locking a row leaves it where it is and simply renders it under the other header.
 * `rank` is the 1-based position among ranked rows, recomputed on read, which makes the
 * contract's contiguous-1..N requirement structural instead of something to maintain.
 *
 * @see resources/js/jobboard-v3/constants/criteria.js
 * @see docs/architecture/job-board/criteria-contract.md
 */

export const isHardOnlyCriterion = (key) => HARD_ONLY_KEYS.includes(key);

/** Keys with no data source yet — shown for legibility, never sent. */
export const isInactiveCriterion = (key) => INACTIVE_KEYS.includes(key);

export const buildDefaultCriteria = () => DEFAULT_CRITERIA_ORDER.map((key) => ({
	key,
	userBucket: isHardOnlyCriterion(key) ? CRITERION_BUCKET.HARD : CRITERION_BUCKET.RANKED,
}));

/**
 * The bucket a row actually sits in: what the user chose, unless its nature or an active
 * filter forces it hard. Keeping the user's choice untouched is what makes clearing a
 * filter return the row to whatever it was before, with no snapshot bookkeeping.
 */
export const effectiveBucket = (criterion, lockedKeys = []) => (
	isHardOnlyCriterion(criterion.key) || lockedKeys.includes(criterion.key)
		? CRITERION_BUCKET.HARD
		: criterion.userBucket
);

const hasText = (value) => (value?.trim() ?? '').length > 0;

// An absent range means the caller supplied no filter state at all, not a filtered one.
const isSalaryFiltered = (salaries) => Array.isArray(salaries)
	&& (salaries[0] !== MIN_SALARY || salaries[1] !== MAX_SALARY);

const isExperienceFiltered = (experiences) => Array.isArray(experiences)
	&& (experiences[0] !== MIN_EXPERIENCE || experiences[1] !== MAX_EXPERIENCE);

/**
 * The criteria an active filter forces into Must-haves — an applied filter is by
 * definition non-negotiable for that search.
 *
 * Mirrors the active-filter predicates in `store/job-board.store.js`, extended with
 * `title` and `auto_apply`, which `activeFilterCount`/`activeFilterChips` deliberately
 * leave out of the mobile chip row. `visa` has no filter field and never appears here.
 *
 * @param {object} filterState plain snapshot of the store's flat filter values
 * @returns {string[]}
 */
export function filterLockedKeys(filterState = {}) {
	const active = {
		[CRITERION_KEY.TITLE]: hasText(filterState.jobTitle),
		[CRITERION_KEY.FRESHNESS]: filterState.selectedPosted?.value != null,
		[CRITERION_KEY.LOCATION]: hasText(filterState.location),
		[CRITERION_KEY.SALARY]: isSalaryFiltered(filterState.salaries),
		[CRITERION_KEY.WORK_SETTING]: (filterState.selectedWorkSettings?.length ?? 0) > 0,
		[CRITERION_KEY.INDUSTRY]: (filterState.selectedIndustries?.length ?? 0) > 0,
		[CRITERION_KEY.YOE]: isExperienceFiltered(filterState.experiences),
		[CRITERION_KEY.AUTO_APPLY]: filterState.autoApplyFilter === true,
	};

	return Object.keys(active).filter((key) => active[key]);
}

const optionValues = (items) => ((items?.length ?? 0) > 0 ? items.map((item) => item.value) : null);

const salaryValue = ({ salaries }) => (isSalaryFiltered(salaries)
	? { min: salaries[0], max: salaries[1] }
	: null);

const experienceValue = ({ experiences }) => (isExperienceFiltered(experiences)
	? { min: experiences[0], max: experiences[1], scope: 'total' }
	: null);

const locationValue = ({ location, latitude, longitude, selectedRange }) => {
	if (!hasText(location)) {
		return null;
	}

	return {
		label: location.trim(),
		latitude: latitude != null ? parseFloat(latitude) : null,
		longitude: longitude != null ? parseFloat(longitude) : null,
		radius: selectedRange?.value != null ? parseInt(selectedRange.value, 10) : null,
	};
};

/**
 * The per-key `value` payloads, adapted from the flat filter fields.
 *
 * The panel edits priority and hard/soft status only — values come from the filter bar,
 * so this is the contract's legacy flat-filter adapter map applied client-side. A key
 * with nothing set carries `null`, which the contract treats as inert.
 *
 * @param {object} filterState plain snapshot of the store's flat filter values
 * @returns {object}
 */
export function criteriaValuesFromFilters(filterState = {}) {
	return {
		[CRITERION_KEY.TITLE]: hasText(filterState.jobTitle) ? filterState.jobTitle.trim() : null,
		[CRITERION_KEY.FRESHNESS]: filterState.selectedPosted?.value ?? null,
		[CRITERION_KEY.SALARY]: salaryValue(filterState),
		[CRITERION_KEY.LOCATION]: locationValue(filterState),
		[CRITERION_KEY.WORK_SETTING]: optionValues(filterState.selectedWorkSettings),
		[CRITERION_KEY.INDUSTRY]: optionValues(filterState.selectedIndustries),
		[CRITERION_KEY.YOE]: experienceValue(filterState),
		[CRITERION_KEY.AUTO_APPLY]: filterState.autoApplyFilter === true ? true : null,
	};
}

/**
 * The flat filter fields a criteria payload's values describe — the inverse of
 * `criteriaValuesFromFilters`, and the reason "Recommend for me" can populate anything at all.
 *
 * The panel model carries no values, so a seeded set can only be applied by writing its
 * values back into the filter bar. That has a visible consequence the caller is buying into:
 * a filled filter is an active filter, so `filterLockedKeys` will render every seeded key as
 * a must-have. That is the intended reading — the seeker asked us to build them a search —
 * and clearing a filter returns its row to nice-to-have with no extra bookkeeping.
 *
 * Keys are emitted only for a non-null `value`; the caller decides what an absent key means.
 * Shaped like the payload `restoreSearchFilters` consumes so the store can reuse its option
 * lookups rather than growing a second dialect.
 *
 * @param {{criteria: Array<object>}} payload a server criteria set
 * @returns {object} partial flat filter state
 */
export function filtersFromCriteriaPayload(payload) {
	const valueOf = (key) => (Array.isArray(payload?.criteria) ? payload.criteria : [])
		.find((criterion) => criterion.key === key)?.value ?? null;

	const filters = {};
	const title = valueOf(CRITERION_KEY.TITLE);
	const freshness = valueOf(CRITERION_KEY.FRESHNESS);
	const salary = valueOf(CRITERION_KEY.SALARY);
	const location = valueOf(CRITERION_KEY.LOCATION);
	const workSettings = valueOf(CRITERION_KEY.WORK_SETTING);
	const industries = valueOf(CRITERION_KEY.INDUSTRY);
	const yoe = valueOf(CRITERION_KEY.YOE);

	if (title !== null) {
		filters.jobTitle = title;
	}

	if (freshness !== null) {
		filters.posted = freshness;
	}

	// An open end is the slider's own ceiling, not a missing bound — the contract's `null` max
	// means "no upper limit", which is exactly what the top of the range already represents.
	if (salary !== null) {
		filters.salaries = [salary.min ?? MIN_SALARY, salary.max ?? MAX_SALARY];
	}

	if (yoe !== null) {
		filters.experiences = [yoe.min ?? MIN_EXPERIENCE, yoe.max ?? MAX_EXPERIENCE];
	}

	if (location !== null) {
		filters.location = location.label;
		filters.latitude = location.latitude;
		filters.longitude = location.longitude;
		filters.range = location.radius;
	}

	if (workSettings !== null) {
		filters.workSettings = workSettings;
	}

	if (industries !== null) {
		filters.industries = industries;
	}

	if (valueOf(CRITERION_KEY.AUTO_APPLY) === true) {
		filters.autoApplyFilter = true;
	}

	return filters;
}

/**
 * The versioned wire payload. Ranks are assigned by walking the array in order and
 * counting only ranked rows, so they come out contiguous `1..N` by construction.
 *
 * @returns {{version: number, criteria: Array<object>}}
 */
export function toCriteriaPayload(criteria = [], filterState = {}) {
	const lockedKeys = filterLockedKeys(filterState);
	const values = criteriaValuesFromFilters(filterState);
	let rank = 0;

	return {
		version: CRITERIA_VERSION,
		criteria: criteria
			.filter((criterion) => !isInactiveCriterion(criterion.key))
			.map((criterion) => {
				const bucket = effectiveBucket(criterion, lockedKeys);

				if (bucket === CRITERION_BUCKET.RANKED) {
					rank += 1;
				}

				return {
					key: criterion.key,
					bucket,
					rank: bucket === CRITERION_BUCKET.RANKED ? rank : null,
					value: values[criterion.key] ?? null,
				};
			}),
	};
}

/**
 * Rebuild the panel's array from a saved payload, falling back to defaults for anything
 * the payload does not mention.
 *
 * Must-have order carries no meaning on the wire — the contract gives hard rows no rank
 * because order among them does not affect ranking — so they are hydrated ahead of the
 * ranked stack. A round trip therefore preserves every row's bucket, rank and value, but
 * a hard row's position within the array can shift.
 *
 * @returns {Array<{key: string, userBucket: string}>}
 */
export function fromCriteriaPayload(payload) {
	const incoming = Array.isArray(payload?.criteria) ? payload.criteria : [];
	const bucketByKey = new Map(
		incoming
			.filter((criterion) => DEFAULT_CRITERIA_ORDER.includes(criterion.key))
			.map((criterion) => [criterion.key, criterion.bucket]),
	);

	const rankOf = new Map(
		incoming
			.filter((criterion) => criterion.bucket === CRITERION_BUCKET.RANKED)
			.map((criterion) => [criterion.key, criterion.rank ?? Number.MAX_SAFE_INTEGER]),
	);

	const bucketFor = (key) => {
		if (isHardOnlyCriterion(key)) {
			return CRITERION_BUCKET.HARD;
		}

		return bucketByKey.get(key) === CRITERION_BUCKET.HARD
			? CRITERION_BUCKET.HARD
			: CRITERION_BUCKET.RANKED;
	};

	const isHard = (key) => bucketFor(key) === CRITERION_BUCKET.HARD;
	const bySavedRank = (a, b) => (
		(rankOf.get(a) ?? Number.MAX_SAFE_INTEGER) - (rankOf.get(b) ?? Number.MAX_SAFE_INTEGER)
	);

	const hard = DEFAULT_CRITERIA_ORDER.filter(isHard);
	const ranked = DEFAULT_RANK_ORDER.filter((key) => !isHard(key)).sort(bySavedRank);

	return [...hard, ...ranked].map((key) => ({ key, userBucket: bucketFor(key) }));
}

/**
 * Reactive views and mutators over a criteria array owned by the caller.
 *
 * @param {import('vue').Ref<Array>} criteria the panel's draft array
 * @param {import('vue').MaybeRefOrGetter<string[]>} lockedKeys keys forced hard by an active filter
 */
export function useCriteria(criteria, lockedKeys = []) {
	const locked = computed(() => toValue(lockedKeys) ?? []);

	const rows = computed(() => criteria.value.map((criterion) => {
		const isNatureLocked = isHardOnlyCriterion(criterion.key);
		const isFilterLocked = !isNatureLocked && locked.value.includes(criterion.key);

		return {
			key: criterion.key,
			label: CRITERION_LABELS[criterion.key],
			bucket: effectiveBucket(criterion, locked.value),
			isNatureLocked,
			isFilterLocked,
			isInactive: isInactiveCriterion(criterion.key),
			canToggle: !isNatureLocked && !isFilterLocked,
		};
	}));

	const hardRows = computed(() => rows.value.filter((row) => row.bucket === CRITERION_BUCKET.HARD));

	const rankedRows = computed({
		get: () => rows.value.filter((row) => row.bucket === CRITERION_BUCKET.RANKED),
		// Drag reorders the filtered ranked list, so write the new order back into the
		// master array's ranked slots and leave the hard slots where they are.
		set: (next) => {
			const slots = [];
			criteria.value.forEach((criterion, index) => {
				if (effectiveBucket(criterion, locked.value) === CRITERION_BUCKET.RANKED) {
					slots.push(index);
				}
			});

			const byKey = new Map(criteria.value.map((criterion) => [criterion.key, criterion]));
			const updated = [...criteria.value];

			next.forEach((row, position) => {
				updated[slots[position]] = byKey.get(row.key);
			});

			criteria.value = updated;
		},
	});

	const toggleBucket = (key) => {
		if (!rows.value.find((row) => row.key === key)?.canToggle) {
			return;
		}

		criteria.value = criteria.value.map((criterion) => (criterion.key === key
			? {
				...criterion,
				userBucket: criterion.userBucket === CRITERION_BUCKET.HARD
					? CRITERION_BUCKET.RANKED
					: CRITERION_BUCKET.HARD,
			}
			: criterion));
	};

	/**
	 * Move a ranked row to a new position. Returns the new 1-based rank, or null when the
	 * move is out of bounds — the keyboard path uses that to decide whether to announce.
	 */
	const moveRanked = (key, toIndex) => {
		const current = rankedRows.value;
		const fromIndex = current.findIndex((row) => row.key === key);

		if (fromIndex === -1 || toIndex < 0 || toIndex >= current.length || toIndex === fromIndex) {
			return null;
		}

		const next = [...current];
		next.splice(toIndex, 0, next.splice(fromIndex, 1)[0]);
		rankedRows.value = next;

		return toIndex + 1;
	};

	return {
		rows,
		hardRows,
		rankedRows,
		toggleBucket,
		moveRanked,
	};
}
